'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Record, Set as ImmutableSet, List, Map as ImmutableMap } from 'immutable';
import { MINIMUM_TARGETABLE_AGE, MAXIMUM_TARGETABLE_AGE, FBGeolocationTypes, Networks } from 'AdsUI/lib/Constants';
import { getLocationKeysByGeolocationType, getLocationCountryCodes } from 'AdsUI/lib/LeadAdCreationUtils';
import { FormLocaleToLinkedInLocale } from 'AdsUI/lib/LeadFormLocale';
import FilterGroupRecord, { FilterGroupTypes } from './FilterGroupRecord';
import { DetailedTargetingEntityRecord } from './DetailedTargetingRecords';
import AudienceNetworkStateRecord, { TargetingFacetTypes } from '../audiences/AudienceNetworkStateRecord';
import GeolocationRecord from '../audiences/GeolocationRecord';
import FBSavedAudienceRecord from '../audiences/FBSavedAudienceRecord';

const groupAudiencesByLIFacet = audiences => {
  const output = [{
    facet: 'urn:li:adTargetingFacet:audienceMatchingSegments',
    entities: audiences.filter(aud => aud.linkedInTargetingFacetType === TargetingFacetTypes.AUDIENCE_MATCHING_SEGMENTS).map(aud => `urn:li:adSegment:${aud.id}`).toArray()
  }, {
    facet: 'urn:li:adTargetingFacet:dynamicSegments',
    entities: audiences.filter(aud => aud.linkedInTargetingFacetType === TargetingFacetTypes.DYNAMIC_SEGMENTS).map(aud => `urn:li:adSegment:${aud.id}`).toArray()
  }].filter(({
    entities
  }) => entities.length);
  return output;
};

class AdCreationGeneralTargetingRecord extends Record({
  locations: ImmutableSet(),
  filterGroups: List(),
  excludedAudiences: ImmutableSet(),
  ageMin: MINIMUM_TARGETABLE_AGE,
  ageMax: MAXIMUM_TARGETABLE_AGE,
  savedAudience: null,
  estimatedSize: null,
  locale: null,
  specialCategory: null,
  dsaInfo: null,

  hasAudiences() {
    return this.filterGroups.find(group => group.audiences.size > 0);
  },

  getLIRequestData() {
    const excludedAudienceOutput = groupAudiencesByLIFacet(this.excludedAudiences);
    return {
      targetingOptions: {
        locale: FormLocaleToLinkedInLocale[this.locale],
        linkedInTargeting: [[{
          facet: 'urn:li:adTargetingFacet:locations',
          entities: this.locations.map(loc => loc.key).toArray()
        }], ...this.filterGroups.map(group => {
          const targetingFacets = group.detailedTargeting.groupBy(item => `urn:li:adTargetingFacet:${item.facet}`);
          const output = targetingFacets.keySeq().map(facet => ({
            facet,
            entities: targetingFacets.get(facet).map(entity => entity.id).toArray()
          })).toJS().concat(groupAudiencesByLIFacet(group.audiences));
          return output;
        }).toArray().filter(group => group.length)],
        linkedInTargetingExclude: excludedAudienceOutput
      }
    };
  },

  getFBRequestData() {
    if (this.savedAudience) {
      return {
        savedAudienceId: this.savedAudience.id
      };
    }

    const audienceGroup = this.filterGroups.find(group => group.type === FilterGroupTypes.AUDIENCES);
    const detailedTargeting = this.filterGroups.filter(group => group.type === FilterGroupTypes.DETAILED_TARGETING).map(group => group.detailedTargeting.map(targeting => ({
      id: targeting.id,
      name: targeting.name,
      type: targeting.facet || targeting.type,
      audienceSize: targeting.audienceSize
    }))).toJS();
    return Object.assign({}, this.dsaInfo && {
      dsaInfo: this.dsaInfo.toJS()
    }, {
      targetingOptions: {
        countryCodes: getLocationCountryCodes(this.locations),
        countryGroups: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.COUNTRY_GROUP),
        countries: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.COUNTRY),
        regions: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.REGION),
        cities: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.CITY),
        ageMin: this.ageMin,
        ageMax: this.ageMax,
        customAudienceIds: audienceGroup ? audienceGroup.audiences.map(audience => audience.id).toArray() : [],
        excludedCustomAudienceIds: this.excludedAudiences ? this.excludedAudiences.map(audience => audience.id).toArray() : [],
        detailedTargeting
      },
      specialAdCategory: this.specialCategory
    });
  },

  getRequestData(network) {
    switch (network) {
      case Networks.FACEBOOK:
        {
          return this.getFBRequestData();
        }

      case Networks.LINKEDIN:
        {
          return this.getLIRequestData();
        }

      default:
        throw new Error('Unsupported network');
    }
  }

}) {
  static getTargetingFromDraft(network, _ref) {
    let {
      locations = [],
      filterGroups = [],
      excludedAudiences = [],
      savedAudience,
      dsaInfo
    } = _ref,
        rest = _objectWithoutPropertiesLoose(_ref, ["locations", "filterGroups", "excludedAudiences", "savedAudience", "dsaInfo"]);

    return new AdCreationGeneralTargetingRecord(Object.assign({
      locations: ImmutableSet(locations.map(loc => new GeolocationRecord(loc))),
      filterGroups: List(filterGroups.map(group => FilterGroupRecord({
        type: group.type,
        audiences: ImmutableSet(group.audiences.map(audience => new AudienceNetworkStateRecord(audience))),
        detailedTargeting: ImmutableSet(group.detailedTargeting.map(targetingEntity => new DetailedTargetingEntityRecord(targetingEntity)))
      }))),
      savedAudience: savedAudience && FBSavedAudienceRecord(savedAudience),
      excludedAudiences: ImmutableSet(excludedAudiences.map(audience => new AudienceNetworkStateRecord(audience))),
      dsaInfo: dsaInfo && new ImmutableMap(dsaInfo)
    }, rest));
  }

}

export default AdCreationGeneralTargetingRecord;